/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Typed error hierarchy for the envelope parser. The fuzz harness in
 * `tests/fuzz/envelope.fuzz.test.js` asserts that every parse failure
 * surfaces as one of these classes — anything else (raw `Error`,
 * `TypeError`, `RangeError`, undefined) fails the test.
 *
 * Hierarchy:
 *   EnvelopeParseError              ← base; concrete for unclassified parse failures (e.g. malformed JSON)
 *   ├── InvalidBase64Error          ← a base64-encoded field couldn't be decoded
 *   ├── EnvelopeSchemaError         ← Zod schema rejected the structure
 *   └── UnsupportedVersionError     ← `version` field exists but isn't recognised
 */

/** Base class for all envelope parser failures. */
export class EnvelopeParseError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'EnvelopeParseError';
  }
}

/** A base64nopad-encoded field could not be decoded. */
export class InvalidBase64Error extends EnvelopeParseError {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'InvalidBase64Error';
  }
}

/** Input parsed as JSON but failed Zod schema validation. */
export class EnvelopeSchemaError extends EnvelopeParseError {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'EnvelopeSchemaError';
  }
}

/** `version` field is present but is not a recognised envelope version. */
export class UnsupportedVersionError extends EnvelopeParseError {
  /** The version value that was rejected; `undefined` if absent or non-numeric. */
  readonly observedVersion: unknown;
  constructor(observedVersion: unknown) {
    super(`unsupported envelope version: ${String(observedVersion)}`);
    this.name = 'UnsupportedVersionError';
    this.observedVersion = observedVersion;
  }
}
